(function () {
  'use strict';

  class CartBar extends HTMLElement {
    constructor() {
      super();
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundResetATC = this.resetATC.bind(this);
      this.boundOptionChange = this.handleOptionChange.bind(this);
      this.boundScroll = this.toggleScroll.bind(this);
    }

    connectedCallback() {
      this.section = this.closest('product-info');
      this.form = this.section?.querySelector('[data-product-form]');
      if (!this.section || !this.form) return;

      this.formWrapper = (window.theme?.settings?.productPageSticky && this.section.querySelector('[data-form-wrapper]')) || this.form;
      this.image = this.querySelector('[data-cart-bar-image]');
      this.options = this.querySelector('[data-cart-bar-options]');
      this.qty = this.querySelector('[data-cart-bar-qty]');
      this.atcBtn = this.querySelector('[data-cart-bar-add-to-cart]');

      this.addEventListener('click', this.boundHandleClick);
      ['theme:cart:refresh', 'theme:cart-bar:reset-atc-animation'].forEach((e) => document.addEventListener(e, this.boundResetATC));

      this.initOptions();
      this.initQty();
      this.subVariant();
      this.initObserver();
    }

    resetATC() {
      this.atcBtn?.classList.remove('is-loading');
      this.atcBtn?.removeAttribute('disabled');
    }

    handleClick(e) {
      if (e.target.closest('[data-scroll-to-form]')) {
        e.preventDefault();
        const target = this.section.querySelector('.product__block--title');
        if (!target) return;
        target.setAttribute('tabindex', '-1');
        target.focus({preventScroll: true});
        target.scrollIntoView({behavior: 'smooth', block: 'start'});
        return;
      }

      if (e.target.closest('[data-cart-bar-add-to-cart]')) {
        e.preventDefault();
        const mainBtn = this.form.querySelector('[data-add-to-cart]');
        if (!mainBtn || mainBtn.hasAttribute('disabled')) return;

        this.atcBtn.classList.add('is-loading');
        this.atcBtn.setAttribute('disabled', true);

        if (this.form.requestSubmit) this.form.requestSubmit(mainBtn);
        else {
          mainBtn.click();
          // Fallback if click doesn't submit (e.g. if it's just a button, not type=submit, though it should be)
          // But requestSubmit is standard now. Keeping click as primary fallback.
        }
      }
    }

    initOptions() {
      this.options?.querySelectorAll('[data-popout-input]').forEach((input) => input.addEventListener('change', this.boundOptionChange));
    }

    handleOptionChange(e) {
      const id = e.target.getAttribute('data-option-value-id');
      if (!id) return;

      const target = this.section.querySelector(`variant-selects [data-option-value-id="${CSS.escape(id)}"]`);
      if (!target) return;

      const event = new MouseEvent('click', {bubbles: true, cancelable: true});
      event.fromCartBar = true;

      if (target.tagName === 'A' || target.matches('input')) {
        target.dispatchEvent(event);
        if (target.matches('input')) {
          const change = new Event('change', {bubbles: true});
          change.fromCartBar = true;
          target.dispatchEvent(change);
        }
      } else {
        target.closest('label, a, [role="button"], .select-popout__option')?.dispatchEvent(event);
      }
    }

    subVariant() {
      if (!window.subscribe || !window.theme?.PUB_SUB_EVENTS?.variantChange) return;
      this.unsub = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({data}) => {
        const sid = data?.sectionId;
        if (!sid || (sid !== this.section.dataset.section && sid !== this.section.dataset.originalSection)) return;

        this.updateImage(data.variant);
        this.updateOptions(data.variant);
        this.updateBtn(data.variant);
        this.updatePrice(data.variant);
      });
    }

    updateImage(variant) {
      const img = this.image?.querySelector('img');
      const src = variant?.featured_media?.preview_image?.src;
      if (img && src) {
        img.src = `${src}${src.includes('?') ? '&' : '?'}width=160`;
      }
    }

    updateOptions(variant) {
      if (!this.options || !variant?.options) return;

      this.options.querySelectorAll('popout-select').forEach((select) => {
        const input = select.querySelector('[data-popout-input]');
        const idx = parseInt(input?.dataset.index?.replace('option', '') || '', 10);
        if (!input || isNaN(idx)) return;

        const currentVal = variant.options[idx - 1];
        const popoutText = select.querySelector('[data-popout-text]');

        select.querySelectorAll('[data-popout-option]').forEach((opt) => {
          const val = opt.dataset.value;
          const isSelected = val === currentVal;
          const item = opt.closest('li');

          if (isSelected && item) {
            if (popoutText) popoutText.textContent = val;
            input.value = val;

            if (item.dataset.optionValueId) input.setAttribute('data-option-value-id', item.dataset.optionValueId);
            if (item.dataset.productUrl) input.setAttribute('data-product-url', item.dataset.productUrl);

            select.querySelector('.select-popout__item--current')?.classList.remove('select-popout__item--current');
            item.classList.add('select-popout__item--current');
            select.querySelector('[aria-current="true"]')?.removeAttribute('aria-current');
            opt.setAttribute('aria-current', 'true');
          }

          const realId = item?.dataset.optionValueId;
          if (realId) {
            const realOpt = this.section.querySelector(`variant-selects [data-option-value-id="${CSS.escape(realId)}"]`);
            if (realOpt) {
              const isUnavailable = realOpt.closest('.sold-out, .unavailable') || realOpt.classList.contains('unavailable') || realOpt.disabled;
              opt.classList.toggle('unavailable', isUnavailable);
            }
          }
        });
      });
    }

    updateBtn(variant) {
      const btn = this.atcBtn;
      const txt = this.querySelector('[data-add-to-cart-text]');
      if (!btn || !txt || !variant) return;

      const avail = variant.available;
      txt.textContent = window.theme?.strings?.[avail ? 'addToCart' : 'soldOut'] || (avail ? 'Add to cart' : 'Sold out');
      btn.toggleAttribute('disabled', !avail);
    }

    updatePrice(variant) {
      const price = this.querySelector('[data-product-price]');
      if (!price || !variant) return;

      const fmt = (v) => window.theme.formatMoney(v, window.theme?.settings?.currency_code_enable ? window.theme.moneyWithCurrencyFormat : window.theme.moneyFormat);

      price.innerHTML = variant.price === 0 ? window.theme?.strings?.free || 'Free' : fmt(variant.price);

      const onSale = variant.compare_at_price > variant.price;
      price.classList.toggle('product__price--sale', onSale);

      const compare = this.querySelector('.product__price--strike');
      if (compare) {
        compare.textContent = onSale ? fmt(variant.compare_at_price) : '';
        compare.classList.toggle('hidden', !onSale);
      }
    }

    initQty() {
      if (!this.qty) return;
      const barQty = this.qty.querySelector('[data-quantity-field]');
      const mainQty = Array.from(this.section.querySelectorAll('[data-quantity-holder]'))
        .find((el) => !this.contains(el))
        ?.querySelector('[data-quantity-field]');

      if (!barQty || !mainQty) return;

      barQty.value = mainQty.value;
      this.qtySync = false;

      const sync = (src, dest) => {
        if (this.qtySync) return;
        this.qtySync = true;
        dest.value = src.value;
        dest.dispatchEvent(new Event('change', {bubbles: true}));
        this.qtySync = false;
      };

      this.syncMain = () => sync(mainQty, barQty);
      this.syncBar = () => sync(barQty, mainQty);

      [mainQty, barQty].forEach((el) => ['input', 'change'].forEach((e) => el.addEventListener(e, el === mainQty ? this.syncMain : this.syncBar)));
      this.mainQtyRef = mainQty;
      this.barQtyRef = barQty;
    }

    initObserver() {
      if ('IntersectionObserver' in window && this.formWrapper) {
        this.obs = new IntersectionObserver(
          (e) => {
            e.forEach((entry) => this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0));
          },
          {rootMargin: '0px', threshold: 0}
        );
        this.obs.observe(this.formWrapper);
      } else {
        document.addEventListener('theme:scroll', this.boundScroll);
      }
    }

    toggleScroll() {
      if (this.formWrapper) {
        this.classList.toggle('is-visible', window.scrollY > this.formWrapper.offsetTop + this.formWrapper.offsetHeight);
      }
    }

    disconnectedCallback() {
      ['theme:cart:refresh', 'theme:cart-bar:reset-atc-animation'].forEach((e) => document.removeEventListener(e, this.boundResetATC));
      if (this.obs) this.obs.disconnect();
      else document.removeEventListener('theme:scroll', this.boundScroll);

      this.unsub?.();
      this.removeEventListener('click', this.boundHandleClick);

      this.options?.querySelectorAll('[data-popout-input]').forEach((i) => i.removeEventListener('change', this.boundOptionChange));

      if (this.mainQtyRef) ['input', 'change'].forEach((e) => this.mainQtyRef.removeEventListener(e, this.syncMain));
      if (this.barQtyRef) ['input', 'change'].forEach((e) => this.barQtyRef.removeEventListener(e, this.syncBar));
    }
  }

  if (!customElements.get('cart-bar')) customElements.define('cart-bar', CartBar);

})();
//# sourceMappingURL=cart-bar.js.map
